import type { ID } from "@services/sdk/types";
import type { Moment } from "moment";
import moment from "moment-timezone";
import { TIMEZONE } from "./constants";

// NB: Tstamps are defined as strings given in AEST
interface IServiceConstructorParams {
	serviceId: ID;
	msisdn: string | null;
	sim: string;
	activationInProgress: boolean;
	activationFailed: boolean;
	isRoaming?: boolean;
	debtOutstanding?: number;
	hasProducts?: boolean;
	isScheduled?: boolean;
	lastScheduledPaymentFailure?: string;
	walletBalance: number;
	status: string;
	rafCode: string;
	activationNetworkStatus?: string;
	portin?: {
		msisdn: string;
		code?: string;
		message?: string;
		portFailureCount: number;
		prePortFailureCount: number;
		isInEmbargo: boolean;
		portInitiatedTstamp?: string | Date;
		portCompletedTstamp?: string | Date;
		prePortSuccessTstamp?: string | Date;
		portFailedTstamp?: string | Date;
	};
}

class Service {
	public static EmptyConstructorParams: IServiceConstructorParams = {
		serviceId: null,
		msisdn: null,
		sim: "",
		status: "",
		activationInProgress: false,
		activationFailed: false,
		walletBalance: 0,
		rafCode: "",
	};

	responseData: IServiceConstructorParams;

	public static ConstructEmpty(): Service {
		return Service.Construct(Service.EmptyConstructorParams);
	}

	public static Construct(params: IServiceConstructorParams): Service {
		return new Service(params);
	}

	public static PortingTimeoutMinutes = 120;

	constructor(params: IServiceConstructorParams) {
		this.responseData = params;
	}

	get id(): ID {
		return this.responseData.serviceId;
	}

	get msisdn(): string | null {
		if (!this.responseData) {
			return null;
		}

		// return portin.msisdn if the user is PORT
		if (this.responseData.portin) {
			return this.responseData.portin.msisdn;
		}
		// return msisdn if the user is NEW_NUMBER
		if (this.responseData.msisdn) {
			return this.responseData.msisdn;
		}

		return null;
	}

	get portInitiatedTstamp(): number | null {
		if (
			!this.responseData.portin ||
			!this.responseData.portin.portInitiatedTstamp
		) {
			return null;
		}

		const m = moment.tz(this.responseData.portin.portInitiatedTstamp, TIMEZONE);

		return m.valueOf();
	}

	get isActivationInProgress(): boolean {
		return this.id !== null && !this.isActivationCompleted;
	}

	get isActivationCompleted(): boolean {
		return (
			this.id !== null &&
			!this.responseData.activationInProgress &&
			!this.responseData.activationFailed &&
			((this.hasProducts && this.isScheduled) ||
				(!this.hasProducts && !this.isScheduled) ||
				(this.hasProducts && !this.isScheduled))
		);
	}

	get isActivationFailed(): boolean {
		return this.id !== null && this.responseData.activationFailed;
	}

	get isPrePortInProgress(): boolean {
		return this.isActivationInProgress && !this.isPrePortCompleted;
	}

	get isPrePortCompleted(): boolean {
		if (this.responseData.portin === undefined) {
			return false;
		}

		return this.responseData.portin.prePortSuccessTstamp !== undefined;
	}

	get isPrePortFailed(): boolean {
		if (this.responseData.portin === undefined) {
			return false;
		}

		return (
			this.isActivationInProgress &&
			!this.isPrePortCompleted &&
			this.prePortFailureCount > 0
		);
	}

	get prePortFailureCount(): number {
		if (this.responseData.portin === undefined) {
			return 0;
		}

		return this.responseData.portin.prePortFailureCount;
	}

	get portFailureCount(): number {
		if (
			this.responseData.portin === undefined ||
			this.responseData.portin.portFailureCount === undefined
		) {
			return 0;
		}

		return this.responseData.portin.portFailureCount;
	}

	get isInEmbargo(): boolean {
		if (this.responseData.portin === undefined) {
			return false;
		}

		return this.responseData.portin.isInEmbargo;
	}

	get isPortingInProgress(): boolean {
		if (this.responseData.portin === undefined) {
			return false;
		}

		return (
			this.responseData.portin.portInitiatedTstamp !== undefined &&
			this.isActivationInProgress
		);
	}

	get isPortingTimedOut(): boolean {
		// checks if portin is undefined or does not have portInitiatedTstamp
		if (this.responseData.portin === undefined || !this.portInitiatedTstamp) {
			return false;
		}

		const diff = moment().diff(moment(this.portInitiatedTstamp), "minutes");
		return diff >= Service.PortingTimeoutMinutes;
	}

	get isPortingCompleted(): boolean {
		if (this.responseData.portin === undefined) {
			return false;
		}

		return (
			this.isActivationCompleted &&
			this.responseData.portin.portCompletedTstamp !== undefined
		);
	}

	get isPortingFailed(): boolean {
		if (this.responseData.portin === undefined) {
			return false;
		}

		return this.isActivationFailed;
	}

	get portingFailureCode(): string {
		if (
			this.responseData.portin === undefined ||
			this.responseData.portin.code === undefined
		) {
			return "";
		}

		return this.responseData.portin.code;
	}

	get debtOutstanding(): number {
		if (!this.responseData.debtOutstanding) {
			return 0;
		}

		return this.responseData.debtOutstanding;
	}

	get lastScheduledPaymentFailureDate(): Moment | null {
		if (!this.responseData.lastScheduledPaymentFailure) {
			return null;
		}

		return moment(this.responseData.lastScheduledPaymentFailure);
	}

	get hasWalletBalance(): boolean {
		if (!this.responseData.walletBalance) {
			return false;
		}

		return !!this.responseData.walletBalance;
	}

	get walletBalance(): number {
		if (!this.responseData.walletBalance) {
			return 0;
		}

		return this.responseData.walletBalance;
	}

	get isRoaming(): boolean {
		return !!this.responseData.isRoaming;
	}

	get hasProducts(): boolean {
		return !!this.responseData.hasProducts;
	}

	get isScheduled(): boolean {
		return !!this.responseData.isScheduled;
	}

	get isSIMSuspended(): boolean {
		return this.responseData.status === "SUSPENDED";
	}

	get referralCode(): string {
		return this.responseData.rafCode;
	}

	get activationNetworkStatus(): string {
		const val = this.responseData.activationNetworkStatus;

		if (!val) {
			return "";
		}

		return val;
	}
}

export default Service;
